"use client";
import { Badge } from "@/intent-components/ui/badge";
import { Button } from "@/intent-components/ui/button";
import { Choicebox } from "@/intent-components/ui/choicebox";
import { DatePicker } from "@/intent-components/ui/date-picker";
import { Label } from "@/intent-components/ui/field";
import { Form } from "@/intent-components/ui/form";
import { Loader } from "@/intent-components/ui/loader";
import { Modal } from "@/intent-components/ui/modal";
import { MultipleSelect } from "@/intent-components/ui/multiple-select";
import { TextField } from "@/intent-components/ui/text-field";
import { IconCheck } from "@intentui/icons";
import { CalendarDate, parseDate } from "@internationalized/date";
import { useRouter } from "next/navigation";
import { startTransition, useState } from "react";
import { Selection } from "react-aria-components";
import { useListData } from "react-stately";
import { saveConfiguration } from "./save-configuration.action";

export type ProfileSelection = {
  id: string;
  name: string;
  countryCode: string;
};

export type ExistingConfiguration = {
  id: number;
  name: string;
  spreadsheetUrl: string;
  reportType: string;
  profiles: ProfileSelection[];
  startDate: string | null;
};

type AddOrCreateClientProps = {
  allProfiles: Array<ProfileSelection>;
  existingConfiguration?: ExistingConfiguration;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
};

export function AddOrCreateClient({
  allProfiles,
  existingConfiguration,
  isOpen,
  onOpenChange,
}: AddOrCreateClientProps) {
  const router = useRouter();

  const [formValues, setFormValues] = useState({
    name: existingConfiguration?.name ?? "",
    spreadsheetUrl: existingConfiguration?.spreadsheetUrl
      ? `https://docs.google.com/spreadsheets/d/${existingConfiguration?.spreadsheetUrl}`
      : "",
    reportType: existingConfiguration?.reportType ?? "daily",
    startDate: existingConfiguration?.startDate
      ? parseDate(existingConfiguration.startDate)
      : null,
  });

  const [isSaving, setIsSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});

  const [genericError, setGenericError] = useState<string | null>(null);

  const handleSaveConfiguration = async () => {
    setGenericError(null);
    setIsSaving(true);
    const result = await saveConfiguration({
      existingConfigurationId: existingConfiguration?.id,
      name: formValues.name,
      spreadsheetUrl: formValues.spreadsheetUrl,
      reportType: formValues.reportType,
      startDate: formValues.startDate?.toString() ?? null,
      profiles: selectedProfiles.items.map((profile) => ({
        name: profile.name,
        region: profile.countryCode,
        profileId: profile.id,
      })),
    });

    if (result?.kind === "field-required") {
      setValidationErrors({
        [result.field]: "This field is required",
      });
    } else if (result?.kind === "cannot-access-spreadsheet") {
      setValidationErrors({
        spreadsheetUrl:
          "Cannot access spreadsheet, please share with sheets-access@artghost.iam.gserviceaccount.com",
      });
    } else if (result?.kind === "generic-error") {
      setGenericError(result.message);
    } else if (result.kind === "success") {
      startTransition(() => {
        setIsSaving(false);
        router.refresh();
        onOpenChange(false);
      });
    }
  };

  const selectedProfiles = useListData<ProfileSelection>({
    initialItems: existingConfiguration?.profiles ?? [],
  });

  const handleStartDateChange = (startDate: CalendarDate | null) => {
    setFormValues({ ...formValues, startDate });
  };

  const handleNameChange = (name: string) => {
    setFormValues({ ...formValues, name });
  };

  const handleSpreadsheetUrlChange = (spreadsheetUrl: string) => {
    setFormValues({ ...formValues, spreadsheetUrl });
  };

  const handleReportTypeChange = (selectedKeys: Selection) => {
    const reportType = [...selectedKeys][0];
    if (!reportType) return;
    setFormValues({ ...formValues, reportType: reportType.toString() });
  };

  return (
    <Modal isOpen={isOpen} onOpenChange={onOpenChange}>
      <Modal.Content>
        <Form
          onSubmit={handleSaveConfiguration}
          validationErrors={validationErrors}
        >
          <Modal.Header>
            <Modal.Title>
              {existingConfiguration ? "Edit" : "Add"} Client
            </Modal.Title>
          </Modal.Header>

          <Modal.Body>
            <div className="flex flex-col gap-y-4">
              {genericError && (
                <Label className="text-(--color-danger)">{genericError}</Label>
              )}

              <TextField
                placeholder="e.g. Laxmi"
                label="Name"
                name="name"
                onChange={handleNameChange}
                value={formValues.name}
              />
              <TextField
                placeholder="e.g. https://docs.google.com/spreadsheets/d/10oeiNGSEzw9TrlFlx5ViaAh_t5uliWtXoO0uwnG9meg/edit?gid=783045845#gid=783045845"
                label="Spreadsheet URL"
                name="spreadsheetUrl"
                type="url"
                onChange={handleSpreadsheetUrlChange}
                value={formValues.spreadsheetUrl}
              />

              <div className="flex flex-col gap-y-1">
                <Label>Report Type</Label>
                <Choicebox
                  aria-label="Select report type"
                  selectionMode="single"
                  selectedKeys={[formValues.reportType]}
                  onSelectionChange={handleReportTypeChange}
                >
                  <Choicebox.Item
                    id="daily"
                    title="Daily"
                    description="A daily report of the last 14 days of data from Amazon."
                  />
                  <Choicebox.Item
                    id="lifetime"
                    title="Lifetime"
                    description="A daily report of all data aggregated over the lifetime of the account."
                  />
                </Choicebox>
              </div>

              {formValues.reportType === "daily" && (
                <DatePicker
                  label="Start Date"
                  name="startDate"
                  onChange={handleStartDateChange}
                  value={formValues.startDate}
                />
              )}

              <MultipleSelect<ProfileSelection>
                label="Profiles"
                selectedItems={selectedProfiles}
                items={allProfiles}
                tag={(item) => (
                  <MultipleSelect.Tag textValue={item.name}>
                    {item.name} - {item.countryCode.toUpperCase()}
                  </MultipleSelect.Tag>
                )}
              >
                {(item: ProfileSelection) => {
                  return (
                    <MultipleSelect.Option
                      textValue={`${item.name} - ${item.countryCode}`}
                    >
                      <div className="mr-2">
                        <Badge className="uppercase">{item.countryCode}</Badge>
                      </div>
                      <span>{item.name}</span>
                    </MultipleSelect.Option>
                  );
                }}
              </MultipleSelect>
            </div>
          </Modal.Body>

          <Modal.Footer>
            <Modal.Close>Cancel</Modal.Close>
            <Button intent="primary" onPress={handleSaveConfiguration}>
              {isSaving ? <Loader /> : <IconCheck />}
              Save
            </Button>
          </Modal.Footer>
        </Form>
      </Modal.Content>
    </Modal>
  );
}
